import { EmissionFactorScope, EmissionFactorType, getEmissionFactorScope } from "../entities/emissionFactor";
import { EnvironmentalCategory, EnvironmentalMetricType, EnvironmentalSubCategory, EnvironmentalTargetType, isKnownEnvironmentalLeaf, isOtherEnvironmentalSubCategory, metricBySubCategory, subCategoryByCategory } from "../entities/environmentalCommitment";
const toNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};
const getDefaultMetricForCategory = (category) => {
    const firstNonOtherSubCategory = (subCategoryByCategory[category] || []).find((subCategory) => !isOtherEnvironmentalSubCategory(subCategory));
    return ((firstNonOtherSubCategory
        ? metricBySubCategory[firstNonOtherSubCategory]
        : undefined) ?? EnvironmentalMetricType.Tonnes);
};
export const getMetricTypeForEnvironmentalSelection = (args) => metricBySubCategory[args.subCategory] ??
    getDefaultMetricForCategory(args.category);
export const getPrimaryEnvironmentalSubCategory = (args) => args.subCategories.find((subCategory) => !isOtherEnvironmentalSubCategory(subCategory)) ??
    args.subCategories[0] ??
    subCategoryByCategory[args.category][0];
export const emptyEmissionsByScope = () => ({
    scope1: 0,
    scope2: 0,
    scope3: 0
});
/** Everything a leaf emitted, across all three scopes. */
export const emissionsByScopeTotal = (e) => e.scope1 + e.scope2 + e.scope3;
/** Accumulate `from` into `into`, in place. */
export const addEmissionsByScope = (into, from) => {
    into.scope1 += from.scope1;
    into.scope2 += from.scope2;
    into.scope3 += from.scope3;
    return into;
};
const EMISSION_FACTOR_TYPES = new Set(Object.values(EmissionFactorType));
const MATERIALS_CATEGORY_TO_FACTOR = new Map([
    [
        EnvironmentalCategory.MaterialsConcreteReadyMix,
        EmissionFactorType.MaterialsConcreteReadyMix
    ],
    [
        EnvironmentalCategory.MaterialsPrecastConcrete,
        EmissionFactorType.MaterialsPrecastConcrete
    ],
    [EnvironmentalCategory.MaterialsAsphalt, EmissionFactorType.MaterialsAsphalt],
    [EnvironmentalCategory.MaterialsBitumen, EmissionFactorType.MaterialsBitumen],
    [
        EnvironmentalCategory.MaterialsWoodTimber,
        EmissionFactorType.MaterialsWoodTimber
    ],
    [EnvironmentalCategory.MaterialsCement, EmissionFactorType.MaterialsCement],
    [
        EnvironmentalCategory.MaterialsAggregates,
        EmissionFactorType.MaterialsAggregates
    ],
    [EnvironmentalCategory.MaterialsSteel, EmissionFactorType.MaterialsSteel],
    [
        EnvironmentalCategory.MaterialsSteelCoil,
        EmissionFactorType.MaterialsSteelCoil
    ],
    [EnvironmentalCategory.MaterialsGlass, EmissionFactorType.MaterialsGlass],
    [EnvironmentalCategory.MaterialsPiping, EmissionFactorType.MaterialsPiping],
    [
        EnvironmentalCategory.MaterialsAluminium,
        EmissionFactorType.MaterialsAluminium
    ],
    [
        EnvironmentalCategory.MaterialsPlasticsSheetsFilm,
        EmissionFactorType.MaterialsPlasticsSheetsFilm
    ],
    [
        EnvironmentalCategory.MaterialsComposites,
        EmissionFactorType.MaterialsComposites
    ],
    [
        EnvironmentalCategory.MaterialsCoatingsFinishes,
        EmissionFactorType.MaterialsCoatingsFinishes
    ],
    [EnvironmentalCategory.MaterialsCables, EmissionFactorType.MaterialsCables],
    [
        EnvironmentalCategory.MaterialsWaterTretmentChemicals,
        EmissionFactorType.MaterialsWaterTretmentChemicals
    ],
    [EnvironmentalCategory.MaterialsCarpet, EmissionFactorType.MaterialsCarpet]
]);
/**
 * Energy by sub-category — the only thing an energy leaf's factor depends on.
 *
 * Fuels are one category (Fuel Type) since the client's taxonomy, so the
 * category says nothing; the leaf says everything. Keyed this way the old
 * mistakes cannot come back: every diesel used to resolve to fossil `Diesel`,
 * over-reporting B100 by roughly 31× (0.0865 against 2.7163 kg CO₂e/L at the
 * rows we default from, because biodiesel's CO₂ is biogenic); E10 was costed as
 * neat petrol; and lubricants, greases and solvents had no factor at all.
 *
 * The blends (B5, B10, B20, B50, E10) resolve to their dominant FOSSIL
 * component here and are mixed with their bio share by `FUEL_BLENDS` at
 * calculation time, so scope derivation and the drawer both still make sense.
 * Lubricating and transmission oils share one factor; the five oil-and-solvent
 * leaves otherwise span 0.0181 to 2.4056 kg CO₂e/L, which is why none of them
 * can borrow another's.
 *
 * Absent on purpose: Acetylene and Hydrogen (the standard we default from
 * publishes no row for either) and every `Other`, including Electricity's — a
 * leaf someone named themselves has no factor we could know. They report
 * quantities and emit nothing rather than a borrowed figure.
 */
const ENERGY_SUB_TO_FACTOR = new Map([
    [EnvironmentalSubCategory.ElectricityKwh, EmissionFactorType.Electricity],
    [EnvironmentalSubCategory.Diesel, EmissionFactorType.Diesel],
    [EnvironmentalSubCategory.DieselPremium, EmissionFactorType.Diesel],
    [EnvironmentalSubCategory.DieselB5Biodiesel, EmissionFactorType.Diesel],
    [EnvironmentalSubCategory.DieselB10Biodiesel, EmissionFactorType.Diesel],
    [EnvironmentalSubCategory.DieselB20Biodiesel, EmissionFactorType.Diesel],
    [EnvironmentalSubCategory.DieselB50Biodiesel, EmissionFactorType.Diesel],
    [
        EnvironmentalSubCategory.DieselB100Biodiesel,
        EmissionFactorType.DieselBiodiesel
    ],
    [EnvironmentalSubCategory.Petrol, EmissionFactorType.Petrol],
    [EnvironmentalSubCategory.PetrolE10, EmissionFactorType.Petrol],
    [EnvironmentalSubCategory.GasLpg, EmissionFactorType.Lpg],
    [EnvironmentalSubCategory.GasNatural, EmissionFactorType.NaturalGas],
    [EnvironmentalSubCategory.GasLng, EmissionFactorType.NaturalGas],
    [EnvironmentalSubCategory.GasCng, EmissionFactorType.NaturalGas],
    [EnvironmentalSubCategory.OilHVO, EmissionFactorType.RenewableDiesel],
    [EnvironmentalSubCategory.OilLubricating, EmissionFactorType.Lubricants],
    [
        EnvironmentalSubCategory.OilTransmissionBrakeHydraulic,
        EmissionFactorType.Lubricants
    ],
    [EnvironmentalSubCategory.OilGrease, EmissionFactorType.Greases],
    [EnvironmentalSubCategory.OilSolvents, EmissionFactorType.Solvents]
]);
/** Non-road transport modes (Road uses sub_category map below). */
const TRANSPORT_CATEGORY_TO_FACTOR = new Map([
    [EnvironmentalCategory.TransportRail, EmissionFactorType.TransportRail],
    [EnvironmentalCategory.TransportShip, EmissionFactorType.TransportSea]
]);
const TRANSPORT_ROAD_SUB_TO_FACTOR = new Map([
    [
        EnvironmentalSubCategory.TransportRoadRigidTruck,
        EmissionFactorType.TransportRoadRigidTruck
    ],
    [
        EnvironmentalSubCategory.TransportRoadArticulatedTruck,
        EmissionFactorType.TransportRoadArticulatedTruck
    ],
    [
        EnvironmentalSubCategory.TransportRoadOther,
        EmissionFactorType.TransportOther
    ]
]);
const WASTE_CATEGORY_TO_FACTOR = new Map([
    [
        EnvironmentalCategory.ConstructionDemolitionWaste,
        EmissionFactorType.WasteConstructionDemolition
    ],
    [
        EnvironmentalCategory.SolidLiquidMixedWaste,
        EmissionFactorType.WasteSolidLiquidMixed
    ],
    [
        EnvironmentalCategory.SpoilFillSoilMaterial,
        EmissionFactorType.WasteSpoilFillSoilMaterial
    ],
    [EnvironmentalCategory.OfficeWaste, EmissionFactorType.WasteOffice]
]);
/**
 * Builds a lookup map from project emission_factor rows (API/RDS shape).
 */
export const emissionFactorRowsToValues = (rows) => {
    const out = {};
    for (const r of rows || []) {
        const t = r.emission_factor_type;
        if (typeof t === "string" && EMISSION_FACTOR_TYPES.has(t)) {
            out[t] = toNumber(r.value);
        }
    }
    return out;
};
export const getEmissionFactorValueForType = (factors, type) => {
    if (!type) {
        return 0;
    }
    return toNumber(factors[type]);
};
/**
 * Derives the emission factor type for a commitment from target/category/sub-category.
 * Call when creating or updating environmental targets (hidden from user).
 */
export const getEmissionFactorTypeForEnvironmentalCommitment = (args) => {
    const { category, sub_category, target_type } = args;
    switch (target_type) {
        case EnvironmentalTargetType.WaterUsage:
            return undefined;
        case EnvironmentalTargetType.EnergyAndFuelsAndLubricants:
            // By leaf alone: an unmapped leaf (Acetylene, Hydrogen, any Other) has
            // no factor, and there is deliberately no category-level fallback.
            return ENERGY_SUB_TO_FACTOR.get(sub_category);
        case EnvironmentalTargetType.Materials:
            return MATERIALS_CATEGORY_TO_FACTOR.get(category);
        case EnvironmentalTargetType.Transport: {
            if (category === EnvironmentalCategory.TransportRoad) {
                return (TRANSPORT_ROAD_SUB_TO_FACTOR.get(sub_category) ??
                    EmissionFactorType.TransportOther);
            }
            return TRANSPORT_CATEGORY_TO_FACTOR.get(category);
        }
        case EnvironmentalTargetType.Waste:
            return WASTE_CATEGORY_TO_FACTOR.get(category);
        default:
            return undefined;
    }
};
export const getEnvironmentalMetricTypeForCommitment = (args) => getMetricTypeForEnvironmentalSelection({
    category: args.category,
    subCategory: args.sub_category
});
/**
 * The Scope 3 well-to-pump / transmission-loss partner of each Scope 1 or
 * Scope 2 factor.
 *
 * A commitment stores ONE factor type, but one reported quantity produces
 * emissions in two scopes: burning the fuel, and producing and delivering it.
 * This map is how the second is found from the first.
 *
 * Absent = the standard publishes no upstream factor for that fuel, which is
 * different from publishing zero. Biodiesel and renewable diesel are the
 * deliberate omissions: "Scope 3 factors for biofuels are highly dependent on
 * individual plant and project characteristics and therefore have not been
 * estimated." Ethanol is the same omission for the same reason, so an E10
 * leaf's upstream share scales with its fossil 0.9. Lubricants, greases and
 * solvents do have published upstream factors but no matching leaf reports them
 * separately today, so they are left out rather than guessed.
 */
export const PRIMARY_TO_UPSTREAM = new Map([
    [EmissionFactorType.Diesel, EmissionFactorType.DieselUpstream],
    [EmissionFactorType.Petrol, EmissionFactorType.PetrolUpstream],
    [EmissionFactorType.Lpg, EmissionFactorType.LpgUpstream],
    [EmissionFactorType.NaturalGas, EmissionFactorType.NaturalGasUpstream],
    [EmissionFactorType.Electricity, EmissionFactorType.ElectricityUpstream]
]);
/**
 * Blended fuels, as fractions of the reported volume.
 *
 * B5, B10, B20 and B50 are NOT published rows in the standard — the fossil and
 * biodiesel rows are, and a blend is a mixture of the two. The arithmetic lives
 * here, as data, so that "why is B20 that number?" has an answer in the
 * codebase rather than in someone's memory. Workstream C moves these ratios into the Globals
 * table, where an admin can see and version them; until then this map is the
 * inspectable record.
 *
 * The names are the blend's own: B5 is 5% biodiesel by volume, B10 10%, B20
 * 20%, B50 50%. Only the fossil share has an upstream partner
 * (`PRIMARY_TO_UPSTREAM`), so a B50 leaf's `DieselUpstream` is weighted 0.5.
 */
export const FUEL_BLENDS = new Map([
    [
        EnvironmentalSubCategory.DieselB5Biodiesel,
        [
            { factorType: EmissionFactorType.Diesel, weight: 0.95 },
            { factorType: EmissionFactorType.DieselBiodiesel, weight: 0.05 }
        ]
    ],
    [
        EnvironmentalSubCategory.DieselB10Biodiesel,
        [
            { factorType: EmissionFactorType.Diesel, weight: 0.9 },
            { factorType: EmissionFactorType.DieselBiodiesel, weight: 0.1 }
        ]
    ],
    [
        EnvironmentalSubCategory.DieselB20Biodiesel,
        [
            { factorType: EmissionFactorType.Diesel, weight: 0.8 },
            { factorType: EmissionFactorType.DieselBiodiesel, weight: 0.2 }
        ]
    ],
    [
        EnvironmentalSubCategory.DieselB50Biodiesel,
        [
            { factorType: EmissionFactorType.Diesel, weight: 0.5 },
            { factorType: EmissionFactorType.DieselBiodiesel, weight: 0.5 }
        ]
    ],
    [
        EnvironmentalSubCategory.PetrolE10,
        [
            { factorType: EmissionFactorType.Petrol, weight: 0.9 },
            { factorType: EmissionFactorType.PetrolEthanol, weight: 0.1 }
        ]
    ]
]);
/**
 * Every factor a leaf's emissions draw on, with each one's share.
 *
 * One leaf, one reported quantity, up to four contributions: the fuel burned,
 * its blend partner if it is a blend, and each of their upstream partners. The
 * scopes fall out of the factor types (`getEmissionFactorScope`), so nothing
 * here decides a scope.
 *
 * Empty when the leaf has no factor — it emits nothing and is in no scope —
 * and empty when the library no longer holds the leaf at all.
 *
 * ---
 *
 * The factor is DERIVED here, and the row's stored `emission_factor_type` is
 * deliberately not read. It is a cache of this very derivation: exactly one
 * place writes it (`createLeaf` in the category tree), it writes what this
 * derives, and nothing in the app can override it — so it holds no information
 * this does not, and every disagreement between the two is the cache being
 * stale.
 *
 * It went stale twice, both silently:
 *
 *   - The library CHANGED a leaf's factor. B100 Biodiesel resolved to fossil
 *     `Diesel` before the factor was keyed by sub-category; rows written then still
 *     said so, costing biodiesel at 2.7163 kg/L instead of 0.0865 — 31× — and
 *     dragging in `DieselUpstream` besides, inventing a Scope 3 figure the NGA
 *     deliberately does not publish for biofuels.
 *   - The library GAINED a factor for a leaf that had none. Every existing row
 *     for that leaf stored NULL, so it reported ZERO however much was filed
 *     against it. That was lubricants, greases and solvents, and the PDF generator derived
 *     the factor while the analytics slice trusted the NULL — one row, two
 *     numbers, neither surface saying which.
 *
 * Deriving makes both impossible rather than correctable, which is why no
 * back-fill is needed: a stale row reads correctly the next time it is read.
 */
export const getEmissionFactorContributions = (commitment) => {
    // An orphaned leaf draws on nothing. It cannot be seen, switched off, or
    // have its factor entered — the drawer walks the library and never renders
    // it — so counting emissions for it means reporting a figure computed from a
    // factor nobody can review. Worse, the factor still counted toward "X of Y
    // emission factors entered", which then never completed.
    if (!isKnownEnvironmentalLeaf(commitment)) {
        return [];
    }
    const primary = getEmissionFactorTypeForEnvironmentalCommitment(commitment);
    if (!primary) {
        return [];
    }
    const burned = FUEL_BLENDS.get(commitment.sub_category) ?? [
        { factorType: primary, weight: 1 }
    ];
    const upstream = burned.flatMap(({ factorType, weight }) => {
        const partner = PRIMARY_TO_UPSTREAM.get(factorType);
        return partner ? [{ factorType: partner, weight }] : [];
    });
    return [...burned, ...upstream];
};
/**
 * Whether a project has actually entered an emission factor.
 *
 * Deliberately not "is a number": no fuel, grid or material emits nothing, so a
 * factor of zero is an unanswered field rather than an answer. Counting it as
 * entered once let the "X of Y emission factors entered" chip and the Carbon
 * Reporting Incomplete banner clear themselves after a save with nothing typed
 * in — the warning defeated itself.
 *
 * Lives here rather than in the frontend's setup requirements (which now
 * re-exports it) because the backend needs the same rule to decide what to
 * freeze onto a submission. Two copies of a predicate that has already been
 * got wrong once is not a risk worth taking.
 */
export const hasEnteredEmissionFactor = (value) => value !== undefined && value !== null && value > 0;
/**
 * The factor values to freeze onto a submission of this leaf.
 *
 * Narrow, not the whole project: a diesel row is costed by diesel's burning
 * factor and its upstream partner, and storing electricity's beside them would
 * be storing a number that had no part in the figure. `getEmissionFactorContributions`
 * already answers which ones apply — blends included, where two to four are
 * weighed together — so this is that list with its values attached.
 *
 * ONLY factors actually entered. A leaf whose factor is still blank is not
 * "costed at zero", it is unanswered, and freezing a zero would make it
 * permanently so: both QA walkthroughs report data first and enter factors
 * afterwards, and under a frozen zero that month would read 0 t forever. What
 * is absent here stays absent, and {@link factorsForSubmission} resolves it
 * against the project's live factors until someone enters one.
 *
 * The consequence, stated plainly: a contribution that was blank when the row
 * was written is NOT frozen, and follows the project until the next time that
 * row is written. Nothing is lost by it — there was no figure to protect.
 */
export const factorsAppliedFor = (commitment, factors) => {
    const out = {};
    for (const { factorType } of getEmissionFactorContributions(commitment)) {
        const value = factors[factorType];
        if (hasEnteredEmissionFactor(value)) {
            out[factorType] = value;
        }
    }
    return out;
};
/**
 * The factors a stored submission should be costed with.
 *
 * `stamped` is what was frozen onto the row when it was written; `live` is the
 * project's factors as they stand now. Stamped wins wherever it has an answer,
 * which is the entire point — a figure already reported cannot move because
 * someone edited a factor afterwards.
 *
 * Two cases fall through to `live`:
 *
 *   - `stamped` is `undefined`/`null` — a row written before the column
 *     existed. It reads exactly as it always did rather than as zero.
 *   - a contribution is missing FROM `stamped` — it was blank at write time
 *     (see {@link factorsAppliedFor}), so there is no frozen figure to defend
 *     and entering the factor should still make the number appear.
 *
 * An empty object is NOT the same as null and must not be treated as one: `{}`
 * means "costed, and nothing applied", which is a real answer for a leaf that
 * emits nothing.
 *
 * Takes `unknown` deliberately — see the note in the body.
 */
export const factorsForSubmission = (stamped, live) => {
    // `unknown`, not `EmissionFactorValues`, because every caller is handing over
    // a raw jsonb column straight from the database. Validating here rather than
    // at eight call sites is the difference between one coercion and eight
    // chances to forget one.
    if (stamped === null || stamped === undefined)
        return live;
    if (typeof stamped !== "object" || Array.isArray(stamped))
        return live;
    const out = { ...live };
    for (const [type, value] of Object.entries(stamped)) {
        // Unknown keys are dropped rather than trusted: the column is written by
        // this codebase, but a factor type RETIRED since the row was written is a
        // real possibility and is not something to carry into a calculation.
        if (EMISSION_FACTOR_TYPES.has(type) && typeof value === "number") {
            out[type] = value;
        }
    }
    return out;
};
/**
 * The core calculation, and the only one: `basis × Σ(weight × factor) / 1000`,
 * bucketed by each factor's own scope.
 *
 * `basis` is whatever the leaf's quantity multiplies out to — litres, kWh,
 * tonnes, or freight × distance for transport. Every public calculator below
 * routes through here, so the per-scope split and the total can never be
 * derived two different ways.
 */
const emissionsForBasis = (basis, commitment, factors) => {
    const out = emptyEmissionsByScope();
    const quantity = toNumber(basis);
    for (const { factorType, weight } of getEmissionFactorContributions(commitment)) {
        const emissions = (quantity * weight * getEmissionFactorValueForType(factors, factorType)) /
            1000;
        switch (getEmissionFactorScope(factorType)) {
            case EmissionFactorScope.Scope1:
                out.scope1 += emissions;
                break;
            case EmissionFactorScope.Scope2:
                out.scope2 += emissions;
                break;
            case EmissionFactorScope.Scope3:
                out.scope3 += emissions;
                break;
        }
    }
    return out;
};
/** tCO₂e from an energy leaf's reported quantity, split by scope. */
export const calculateEnergyEmissionsTCo2e = (quantity, commitment, factors) => emissionsForBasis(quantity, commitment, factors);
/** tCO₂e from a transport leaf's tonne-kilometres, split by scope. */
export const calculateTransportEmissionsTCo2e = (totalFreight, totalDistance, commitment, factors) => emissionsForBasis(toNumber(totalFreight) * toNumber(totalDistance), commitment, factors);
/**
 * Single entry point: route by environmental target type, and return the split
 * by scope.
 *
 * Only the BASIS differs per target type — which reported number the factors
 * multiply. Everything else, including the scope bucketing, is
 * `emissionsForBasis`, so the four branches cannot drift into four
 * calculations. The waste and materials branches used to inline the same
 * arithmetic three times over.
 *
 * Water is the one target type with no basis at all: the standard publishes no
 * water emission factor, so it returns zeros in every scope.
 */
export const calculateEnvironmentalReportEmissionsTCo2e = (commitment, reportEntry, factors) => {
    switch (commitment.target_type) {
        case EnvironmentalTargetType.EnergyAndFuelsAndLubricants:
        case EnvironmentalTargetType.Waste:
        case EnvironmentalTargetType.Materials:
            return emissionsForBasis(reportEntry.quantity, commitment, factors);
        case EnvironmentalTargetType.Transport:
            return calculateTransportEmissionsTCo2e(reportEntry.total_freight ?? 0, reportEntry.total_distance ?? 0, commitment, factors);
        case EnvironmentalTargetType.WaterUsage:
        default:
            return emptyEmissionsByScope();
    }
};
